import { SPEC_V1_SHA256, specV1 } from '@equiview/protocol';
import { requestPersistentStorage } from '@equiview/sync';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, ScrollRestoration, useMatches } from 'react-router';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import { env } from '../lib/env.js';
import { DEVICE_ID } from '../lib/local.js';
import { lerModo, trocarModo } from '../lib/modo.js';
import { useTecladoAberto } from '../lib/teclado.js';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../lib/useAuth.js';
import { BarraLateral, type ItemNavegacao } from './BarraLateral.js';
import { avisoDaFaixa, FaixaDeContexto } from './FaixaDeContexto.js';
import { IconeComparar, IconeDocumento, IconeInicio, IconeLista, IconeTabela, IconeUsuario } from './Icones.js';
import { Marca } from './Marca.js';
import { SeletorTema } from './SeletorTema.js';
import { IndicadorSincronia } from './Sincronia.js';
import { Interruptor, ItemMenu, Menu } from './ui/index.js';
import { usePublicoVisao } from './VisoesRapidas.js';

const NAVEGACAO = [
  { para: '/animais', rotulo: 'Plantel', Icone: IconeLista, soEquipe: false },
  { para: '/visitas', rotulo: 'Visitas', Icone: IconeDocumento, soEquipe: true },
  { para: '/comparar', rotulo: 'Comparar', Icone: IconeComparar, soEquipe: false },
  // Consulta, exportacao e repetibilidade: da equipe, e aberto no modo demo (so leitura).
  { para: '/dados', rotulo: 'Dados', Icone: IconeTabela, soEquipe: 'ou-demo' as const },
];

const INICIO: ItemNavegacao = { para: '/', rotulo: 'Início', Icone: IconeInicio, fim: true };

/**
 * Casca de todas as paginas.
 * - A partir de `lg`: barra lateral Abismo (navegacao, visoes rapidas, tema e
 *   demonstracao) e o conteudo ao lado, sem topo.
 * - Abaixo de `lg`: topo Abismo; navegacao no topo de `md` a `lg` e embaixo no
 *   celular, onde o polegar alcanca.
 */
export function Layout() {
  const fonte = useFonte();
  const { session } = useAuth();
  const equipe = usePodeCadastrar();
  const usuario = useConsulta((f) => f.usuarioAtual(), []);
  const admin = !!fonte.cadastro && usuario.estado === 'pronto' && usuario.dado?.papel === 'admin';
  const dono = usuario.estado === 'pronto' && usuario.dado?.papel === 'haras';
  const navegacao = NAVEGACAO.filter(
    (n) => !n.soEquipe || equipe || (n.soEquipe === 'ou-demo' && fonte.ficticia && !dono),
  );
  const modo = lerModo();
  // Da para escolher como ver o app so sem login ou com dados ficticios; com
  // login e dados reais, vale o papel do login.
  const faixa = fonte.ficticia || (env.semLogin && !session);
  const temMenu = !!session || !!fonte.cadastro || !faixa;
  const [persistente, setPersistente] = useState(true);
  // Tela de tarefa (handle das rotas em App.tsx) ou teclado aberto: sem navegacao de baixo.
  const tarefa = useMatches().some((m) => (m.handle as { tarefa?: boolean } | undefined)?.tarefa);
  const teclado = useTecladoAberto();
  const semNavegacaoDeBaixo = tarefa || teclado;
  const publico = usePublicoVisao();

  useEffect(() => {
    // Antes de qualquer coleta: pedir ao navegador que nao descarte o
    // armazenamento. No iOS, sem isso, um PWA nao instalado perde o IndexedDB.
    void requestPersistentStorage().then(setPersistente);
  }, []);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <a
        href="#conteudo"
        className="sr-only z-50 bg-marca px-4 py-2 text-sobre-marca focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Pular para o conteúdo
      </a>

      <BarraLateral
        navegacao={[INICIO, ...navegacao]}
        publico={publico}
        linkHaras={fonte.cadastro ? (equipe ? 'Haras cadastrados' : 'Meus haras') : null}
        admin={admin}
        demonstracao={faixa ? { ficticia: fonte.ficticia, aviso: avisoDaFaixa(fonte.ficticia, env.previa) } : null}
        interruptorDemo={!faixa && temMenu ? { ligado: modo === 'demo' } : null}
        sessao={session ? { email: session.user.email } : null}
        persistente={persistente}
      />

      <div className={`flex min-h-dvh min-w-0 flex-col md:pb-0 ${semNavegacaoDeBaixo ? '' : 'pb-16'}`}>
        <header className="sticky top-0 z-20 bg-topo text-sobre-topo lg:hidden">
          <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4 lg:px-8">
            <NavLink to="/" aria-label="Equiview, início" className="shrink-0">
              <Marca />
            </NavLink>

            <nav aria-label="Principal" className="hidden h-full items-stretch md:flex">
              {navegacao.map(({ para, rotulo }) => (
                <NavLink
                  key={para}
                  to={para}
                  className={({ isActive }) =>
                    `flex items-center border-b-2 px-4 text-sm font-medium transition-colors ${
                      isActive ? 'border-nevoa text-gelo' : 'border-transparent text-nevoa/80 hover:text-gelo'
                    }`
                  }
                >
                  {rotulo}
                </NavLink>
              ))}
            </nav>

            <div className="ml-auto flex items-center gap-1">
              {/* Sem login nada sincroniza: o indicador so confundiria. */}
              {session && <IndicadorSincronia persistente={persistente} />}
              <SeletorTema />
              {temMenu && (
                <Menu
                  rotulo="Menu"
                  classeGatilho="flex size-9 items-center justify-center rounded-(--radius-controle) text-nevoa hover:bg-gelo/8 hover:text-gelo"
                  gatilho={<IconeUsuario />}
                >
                  {(fechar) => (
                    <>
                      {session && <p className="truncate px-3 pt-1 pb-2 text-xs text-apagado">{session.user.email}</p>}
                      {fonte.cadastro && (
                        <Link
                          to="/haras"
                          onClick={fechar}
                          role="menuitem"
                          className="flex min-h-9 items-center px-3 text-sm hover:bg-destaque-fundo"
                        >
                          {equipe ? 'Haras cadastrados' : 'Meus haras'}
                        </Link>
                      )}
                      {admin && (
                        <Link
                          to="/equipe"
                          onClick={fechar}
                          role="menuitem"
                          className="flex min-h-9 items-center px-3 text-sm hover:bg-destaque-fundo"
                        >
                          Equipe e acessos
                        </Link>
                      )}
                      {/* Com a faixa a vista, o interruptor ja esta nela. */}
                      {!faixa && (
                        <Interruptor
                          noMenu
                          ligado={modo === 'demo'}
                          onMudar={(ligado) => trocarModo(ligado ? 'demo' : 'real')}
                        >
                          Dados fictícios
                        </Interruptor>
                      )}
                      {session && (
                        <div className="border-t border-borda pt-1">
                          <ItemMenu
                            onClick={() => {
                              fechar();
                              void supabase.auth.signOut();
                            }}
                          >
                            Sair
                          </ItemMenu>
                        </div>
                      )}
                    </>
                  )}
                </Menu>
              )}
            </div>
          </div>
        </header>

        {faixa && (
          <div className="lg:hidden">
            <FaixaDeContexto ficticia={fonte.ficticia} previa={env.previa} />
          </div>
        )}

        <main id="conteudo" className="mx-auto w-full max-w-[1360px] flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>

        {/*
        Rodape de auditabilidade. O hash do protocolo aparece aqui e em todo
        laudo: e o que permite provar, anos depois, sob qual protocolo cada
        medida foi colhida. Requisito do fiador cientifico.
      */}
        <footer className="border-t border-borda">
          <div className="mx-auto flex max-w-[1360px] flex-wrap justify-between gap-x-6 gap-y-1 px-4 py-4 text-xs text-apagado lg:px-8">
            <span>
              Equiview · Avaliação morfofuncional equina · {env.appVersion}
              {fonte.ficticia && (
                <>
                  {' · '}
                  <Link to="/creditos-fotos" className="underline underline-offset-2 hover:text-destaque-texto">
                    Fotos de exemplo: créditos
                  </Link>
                </>
              )}
            </span>
            <span className="font-mono">
              protocolo {specV1.code} · {SPEC_V1_SHA256.slice(0, 12)} · dispositivo {DEVICE_ID.slice(0, 8)}
            </span>
          </div>
        </footer>
      </div>

      <nav
        aria-label="Principal"
        className={`fixed inset-x-0 bottom-0 z-20 flex border-t border-borda bg-superficie pb-[env(safe-area-inset-bottom)] md:hidden ${
          semNavegacaoDeBaixo ? 'hidden' : ''
        }`}
      >
        {navegacao.map(({ para, rotulo, Icone }) => (
          <NavLink
            key={para}
            to={para}
            className={({ isActive }) =>
              `flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 border-t-2 text-xs font-medium tracking-wide uppercase ${
                isActive ? 'border-marca text-marca' : 'border-transparent text-apagado'
              }`
            }
          >
            <Icone />
            {rotulo}
          </NavLink>
        ))}
      </nav>

      <ScrollRestoration />
    </div>
  );
}
